import { useState, useEffect } from "react";
import FormSection from "../ui/FormSection";
import Button from "../ui/Button";
import FormInput from "../ui/FormInput";
import CustomCheckbox from "../ui/CustomCheckbox";
import MilestoneTable from "../ui/MilestoneTable";
import StudyController from "@/network/controllers/study.controller";
import Modal from "../ui/Modal";

export default function MasterICFSettings({
  study = {},
  onUpdateStudy,
  milestones: initialMilestones = [],
}) {
  const [localData, setLocalData] = useState({
    masterICFName: "",
    selectedVersion: null,
    newMilestone: {
      name: "",
      description: "",
      mandatory: false,
    },
  });
  const [masterICFs, setMasterICFs] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [showMilestoneDeleteErrorModal, setShowMilestoneDeleteErrorModal] =
    useState(false);
  const [milestoneDeleteErrorMessage, setMilestoneDeleteErrorMessage] =
    useState("");

  const [activeVersion, setActiveVersion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingMilestones, setIsEditingMilestones] = useState(false);

  useEffect(() => {
    if (study && Object.keys(study).length > 0) {
      console.log("MasterICFSettings received study:", study);
      const mappedMasterICFs = (study.masterICF || []).map((icf) => ({
        ...icf,
        name: icf.master_icf_name,
      }));
      setMasterICFs(mappedMasterICFs);
      const initialActiveVersion =
        mappedMasterICFs.find((icf) => icf.isActive)?.id ||
        (mappedMasterICFs.length > 0
          ? mappedMasterICFs[mappedMasterICFs.length - 1].id
          : null);
      setActiveVersion(initialActiveVersion);
      setLocalData((prev) => ({
        ...prev,
        masterICFName: "",
        selectedVersion: initialActiveVersion,
      }));
    }
  }, [study]);

  useEffect(() => {
    if (initialMilestones && initialMilestones.length > 0) {
      const sortedMilestones = initialMilestones.map((m, index) => ({
        ...m,
        number: index + 1,
        selected: m.master_icf_id === activeVersion,
      }));
      setMilestones(sortedMilestones);
    }
  }, [initialMilestones, activeVersion]);

  useEffect(() => {
    const fetchMappingsForActiveVersion = async () => {
      if (!activeVersion) return;

      try {
        const mappings = await StudyController.getMasterIcfMappings(
          activeVersion
        );
        console.log("🧩 Raw mappings response:", mappings);

        const updatedMilestones = milestones.map((milestone) => {
          const isSelected = mappings.some(
            (mapping) => mapping.milestone_id === milestone.id
          );
          return {
            ...milestone,
            selected: isSelected,
            master_icf_id: isSelected ? activeVersion : null,
          };
        });

        setMilestones(updatedMilestones);
      } catch (error) {
        console.error("Failed to fetch mappings for ICF:", error);
      }
    };

    fetchMappingsForActiveVersion();
  }, [activeVersion]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMilestoneInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLocalData((prev) => ({
      ...prev,
      newMilestone: {
        ...prev.newMilestone,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const handleCheckboxChange = (isChecked) => {
    setLocalData((prev) => ({
      ...prev,
      newMilestone: {
        ...prev.newMilestone,
        mandatory: isChecked,
      },
    }));
  };

  const handleAddMasterICF = () => {
    if (localData.masterICFName.trim()) {
      const newMasterICF = {
        id: crypto.randomUUID(),
        name: study?.name
          ? `${study.name} ${localData.masterICFName}`
          : localData.masterICFName,
        version: localData.masterICFName,
        createdAt: new Date().toISOString(),
        isActive: masterICFs.length === 0,
      };
      const updatedMasterICFs = [...masterICFs, newMasterICF];
      setMasterICFs(updatedMasterICFs);
      setLocalData((prev) => ({ ...prev, masterICFName: "" }));
      if (masterICFs.length === 0) {
        setActiveVersion(newMasterICF.id);
      }
      if (onUpdateStudy) {
        onUpdateStudy({
          ...study,
          masterICF: updatedMasterICFs,
        });
      }
    }
  };

  const handleVersionChange = (e) => {
    const selectedId = e.target.value;
    setActiveVersion(selectedId);
    const updatedMasterICFs = masterICFs.map((icf) => ({
      ...icf,
      isActive: icf.id === selectedId,
    }));
    setMasterICFs(updatedMasterICFs);
  };

  // TODO: CETTE FONCTION AJOUTE UNE MILESTONE SEULEMENT EN BACKEND
  const handleAddMilestone = async () => {

    setIsLoading(true);
    const { name, description, mandatory } = localData.newMilestone;
    if (!name.trim() || !description.trim() || !activeVersion) {
      console.warn("Missing required milestone data or active ICF version");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const payload = {
      name,
      description,
      mandatory,
      master_icf_id: activeVersion,
    };
    /** ------------------ cmt 600993 ------------------
    const response = await StudyController.addMilestoneToStudyById(
      study.id,
      payload
    );
    *-------------------------------------------------*/

    console.log("=== payload === masterICFsettings.jsx === key: 950819 ===");
    console.dir(payload, { depth: null, colors: true });
    console.log("=================================");

    try {
      const response = await StudyController.addMilestonesToStudy(study.id, [
        payload,
      ]);
      if (response?.success === false) {
        console.error("Error adding milestone:", response.message);
        setIsLoading(false);
        return;
      }
  
      console.log('=== response === masterICFsettings.jsx === key: 545765 ===');
      console.dir(response, { depth: null, colors: true })
      console.log('=================================');
  
      const newMilestone = {
        ...response[0].milestones[0],
        id: response[0].milestones[0].id,
        number: milestones.length + 1,
        name: response[0].milestones[0].title, // title from backend
        description: response[0].milestones[0].short_description, // from backend
        mandatory: response[0].milestones[0].mandatory === true,
      };
      const updatedMilestones = [...milestones, newMilestone];
      setMilestones(updatedMilestones);
      setLocalData((prev) => ({
        ...prev,
        newMilestone: {
          name: "",
          description: "",
          mandatory: false,
        },
      }));
      if (onUpdateStudy) {
        onUpdateStudy({
          ...study,
          milestones: updatedMilestones,
        });
      }
      setIsLoading(false);
    } catch (error) {
      console.log('=== error === masterICFsettings.jsx === key: 921839 ===');
      console.dir(error, { depth: null, colors: true })
      console.log('=================================');
    }
    finally {
      setIsLoading(false);
    }
  };

  //TODO: CETTE FONCTION EDIT UNE MILESTONE SEULEMENT EN BACKEND
  const handleEditMilestone = async (updatedMilestones = []) => {
    const backendUpdatedMilestones = [];

    for (const milestone of updatedMilestones) {
      const { id, name, description, mandatory } = milestone;

      if (!id || !name || !description) {
        console.warn("Skipping invalid milestone update:", milestone);
        continue;
      }

      try {
        const response = await StudyController.updateMilestoneById({
          studyId: study.id,
          milestone_id: id,
          name,
          description,
          mandatory,
        });

        console.log("📡 backend response:", response?.data);

        if (response?.success && response.data?.milestone) {
          const backendMilestone = response.data.milestone;

          backendUpdatedMilestones.push({
            ...milestone,
            name: backendMilestone.title,
            description: backendMilestone.short_description,
            mandatory: backendMilestone.mandatory,
          });
        } else {
          console.error("Error updating milestone:", response?.message);
        }
      } catch (error) {
        console.error("handleEditMilestone error:", error);
      }
    }

    const updated = milestones.map((m) => {
      const updatedMilestone = backendUpdatedMilestones.find(
        (u) => u.id === m.id
      );
      return updatedMilestone ? { ...m, ...updatedMilestone } : m;
    });

    console.log("=== updated === masterICFsettings.jsx === key: 892031 ===");
    console.dir(updated, { depth: null, colors: true });
    console.log("=================================");

    console.log(
      "=== updatedMilestones === masterICFsettings.jsx === key: 223675 ==="
    );
    console.dir(updatedMilestones, { depth: null, colors: true });
    console.log("=================================");

    await handleSaveMilestoneSelections(updatedMilestones);

    setMilestones(updated);

    if (onUpdateStudy) {
      onUpdateStudy({ ...study, milestones: updated });
    }
  };

  //TODO CETTE FONCTION DELETE UNE MILESTONE SEULEMENT EN BACKEND
  const handleDeleteMilestone = async (milestone) => {
    try {
      const response = await StudyController.deactivateMilestoneById(
        study.id,
        milestone.id
      );
      if (response?.success) {
        const filteredMilestones = milestones.filter(
          (m) => m.id !== milestone.id
        );
        const updatedMilestones = filteredMilestones.map((m, index) => ({
          ...m,
          number: index + 1,
        }));
        setMilestones(updatedMilestones);
        if (onUpdateStudy) {
          onUpdateStudy({ ...study, milestones: updatedMilestones });
        }
      } else {
        console.warn(" Backend blocked milestone deletion:", response);
        setMilestoneDeleteErrorMessage(
          response.message ||
            "This milestone cannot be deactivate because it is already used by participants."
        );
        setShowMilestoneDeleteErrorModal(true);
      }
    } catch (error) {
      console.error("handleDeleteMilestone error:", error);
    }
  };

  const handleValidatingChange = (milestoneId, isChecked) => {
    const updatedMilestones = milestones.map((milestone) =>
      milestone.id === milestoneId
        ? { ...milestone, mandatory: isChecked }
        : milestone
    );
    setMilestones(updatedMilestones);
    if (onUpdateStudy) {
      onUpdateStudy({
        ...study,
        milestones: updatedMilestones,
      });
    }
  };

  const handleSelectMilestoneChange = (milestoneId, isChecked) => {
    const updatedMilestones = milestones.map((milestone) =>
      milestone.id === milestoneId
        ? {
            ...milestone,
            selected: isChecked,
            master_icf_id: isChecked ? activeVersion : null,
          }
        : milestone
    );
    setMilestones(updatedMilestones);
    if (onUpdateStudy) {
      onUpdateStudy({
        ...study,
        milestones: updatedMilestones,
      });
    }
  };

  // -----------------------------------------------
  // TODO: SELECTED MILESOTNE TO CALL BACKEDN
  // -----------------------------------------------

  const handleSaveMilestoneSelections = async (updatedMilestones) => {
    try {
      const mappings = (updatedMilestones || milestones)
        .filter((m) => m.selected)
        .map((m) => ({
          studyId: study.id,
          masterIcfId: m.master_icf_id || activeVersion,
          milestoneId: m.id,
        }));

      console.log("=== mappings === masterICFsettings.jsx === key: 833402 ===");
      console.dir(mappings, { depth: null, colors: true });
      console.log("=================================");

      const response = await StudyController.replaceMasterIcfMappings(
        activeVersion,
        mappings
      );

      if (response?.success === false) {
        console.error("Error saving milestone mappings:", response.message);
        setMilestoneDeleteErrorMessage(
          response.message || "Unable to save mappings"
        );
        setShowMilestoneDeleteErrorModal(true);
      } else {
        console.log("Milestone mappings saved successfully");
      }
    } catch (error) {
      console.error("Unexpected error during save:", error.message);
      setMilestoneDeleteErrorMessage("Unexpected error during save");
      setShowMilestoneDeleteErrorModal(true);
    }
  };

  const handleReorderMilestones = (oldIndex, newIndex) => {
    const result = Array.from(milestones);
    const [removed] = result.splice(oldIndex, 1);
    result.splice(newIndex, 0, removed);
    const updatedMilestones = result.map((milestone, index) => ({
      ...milestone,
      number: index + 1,
    }));
    setMilestones(updatedMilestones);
    if (onUpdateStudy) {
      onUpdateStudy({
        ...study,
        milestones: updatedMilestones,
      });
    }
  };

  const handleAddICF = () => {
    if (localData.masterICFName.trim() !== "") {
      const newICF = {
        id: crypto.randomUUID(),
        master_icf_name: localData.masterICFName.trim(),
        name: localData.masterICFName.trim(),
        createdAt: new Date().toISOString(),
        isActive: masterICFs.length === 0,
      };
      const updatedMasterICFs = [...masterICFs, newICF];
      setMasterICFs(updatedMasterICFs);
      setLocalData((prev) => ({ ...prev, masterICFName: "" }));
      if (masterICFs.length === 0) {
        setActiveVersion(newICF.id);
      }
      onUpdateStudy({ ...study, masterICF: updatedMasterICFs });
    }
  };

  const handleDeleteICF = (idToDelete) => {
    const updatedMasterICFs = masterICFs.filter((icf) => icf.id !== idToDelete);
    setMasterICFs(updatedMasterICFs);
    // If the deleted version was active, set a new active version (e.g., the last one)
    if (idToDelete === activeVersion && updatedMasterICFs.length > 0) {
      const lastICF = updatedMasterICFs[updatedMasterICFs.length - 1];
      lastICF.isActive = true;
      setActiveVersion(lastICF.id);
    } else if (updatedMasterICFs.length === 0) {
      setActiveVersion(null);
    }
    onUpdateStudy({ ...study, masterICF: updatedMasterICFs });
  };

  const handleEditICF = (index) => {
    // Logic to handle editing ICF
  };

  const handleUpdateICF = () => {
    // Logic to handle updating ICF
  };

  return (
    <div
      className={`bg-dashboard-bg1 rounded-xl p-4 sm:p-6 ${
        study.status === "closed" ? "pointer-events-none" : ""
      }`}
    >
      {/* Master ICF Section */}
      <FormSection title="Master ICF settings">
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Select master ICF version
          </label>
          {/* Keep full width on mobile, tighter cap on larger screens */}
          <div className="min-w-0">
            <select
              value={activeVersion || ""}
              onChange={handleVersionChange}
              disabled={isEditingMilestones}
              className={`w-full sm:max-w-md rounded-md border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5B93FF] ${
                isEditingMilestones ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""
              }`}
            >
              {masterICFs.length === 0 ? (
                <option value="" disabled>No versions available</option>
              ) : (
                masterICFs.map((icf) => (
                  <option key={icf.id} value={icf.id}>{icf.name}</option>
                ))
              )}
            </select>
          </div>
        </div>
      </FormSection>
  
      {/* Milestones Settings Section */}
      <FormSection title="Milestones settings">
        {/* Table wrapper: horizontal scroll on small screens to reveal Actions */}
        <div className="mb-6 -mx-2 sm:mx-0">
        <div
      className="overflow-x-auto px-3 sm:px-0"
      style={{ scrollbarGutter: 'stable both-edges' }} 
    >
            {/* Set a sensible min width so columns don't collapse */}
            <div className="min-w-[1120px] sm:min-w-[1120px] md:min-w-[1180px]">
              <MilestoneTable
                milestones={milestones}
                onEdit={{ multiple: handleEditMilestone }}
                onDelete={handleDeleteMilestone}
                onValidatingChange={handleValidatingChange}
                onReorder={handleReorderMilestones}
                onSelectChange={handleSelectMilestoneChange}
                showSelectedColumn={true}
                onEditStart={() => setIsEditingMilestones(true)}
                onEditEnd={() => setIsEditingMilestones(false)}
              />
            </div>
          </div>
        </div>
  
        <h3 className="mb-4 text-lg font-semibold">Add milestone</h3>
  
        {/* Inputs align neatly; button never overflows */}
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-4 min-w-0">
            <FormInput
              label="Milestone name"
              name="name"
              value={localData.newMilestone.name}
              onChange={handleMilestoneInputChange}
              placeholder="Ex: Baseline assessment, First treatment administration"
              inputClassName="h-10"
            />
          </div>
          <div className="md:col-span-4 min-w-0">
            <FormInput
              label="Milestone description"
              name="description"
              value={localData.newMilestone.description}
              onChange={handleMilestoneInputChange}
              placeholder="Ex: First dose administered according to the protocol"
              inputClassName="h-10"
            />
          </div>
  
          {/* Keep checkbox vertically centered and same height as inputs */}
          <div className="md:col-span-2 flex flex-col">
            <label
              htmlFor="mandatory"
              className="mb-2 block text-center text-sm font-medium text-gray-700"
            >
              Validating milestone
            </label>
            <div className="flex h-10 items-center justify-center pt-1">
              <CustomCheckbox
                checked={localData.newMilestone.mandatory}
                onChange={handleCheckboxChange}
              />
            </div>
          </div>
  
          {/* Button aligned to the right on wide screens, full width on mobile */}
          <div className="md:col-span-2 flex flex-col">
            <div className="mb-2 h-2" />
            <div className="flex h-12 items-center justify-end">
              <Button
                onClick={handleAddMilestone}
                variant="addbutton"
                className="w-full sm:w-auto"
                disabled={isLoading}
              >
                {isLoading ? "Adding..." : "Add milestone"}
              </Button>
            </div>
          </div>
        </div>
      </FormSection>
  
      <Modal
        isOpen={showMilestoneDeleteErrorModal}
        onClose={() => setShowMilestoneDeleteErrorModal(false)}
        title="Cannot Delete Milestone"
        primaryAction={() => setShowMilestoneDeleteErrorModal(false)}
        primaryActionLabel="Close"
      >
        <p className="mb-4">{milestoneDeleteErrorMessage}</p>
      </Modal>
    </div>
  );
}
